import {
  type Message,
  type ResolvedToolDefinition,
  type ToolCall,
} from '@justrelate/llm-conversation';
import {
  ReactNode,
  RefObject,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DataClass, LinkTag, connect } from 'scrivito';

import type { UserInputSource } from '../Chatbot';
import { LlmConfig, buildTools } from '../llm';
import { ComponentToolDefinition, ToolDefinition } from '../tool_integration';
import { useTranslate } from '../translate';

import { SystemMessage } from './ChatRenderer';
import { PromptHistory } from './PromptHistory';

interface MarkdownContextProps {
  onPromptClicked: (prompt: string) => void;
  dataClasses?: DataClass[];
}

const MarkdownContext = createContext<MarkdownContextProps | null>(null);

interface ChatBodyProps {
  chatLogRef: RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  showHistory?: boolean;
  messages: Message[];
  promptHistory?: string[];
  runPrompt: (prompt: string, source: UserInputSource) => void;
  systemMessage: SystemMessage | null;
  config: LlmConfig;
}

export function ChatBody({
  chatLogRef,
  isLoading,
  showHistory,
  messages,
  promptHistory,
  runPrompt,
  config,
  systemMessage,
}: ChatBodyProps) {
  const componentTools = buildComponentTools(config.tools);
  const tools = useMemo(() => buildTools(config), [config]);

  const translate = useTranslate();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setScrolledToBottom] = useState(true);

  useEffect(() => {
    const bottom = bottomRef.current;
    if (!bottom) return;

    const observer = new IntersectionObserver(
      ([entry]) => setScrolledToBottom(entry.isIntersecting),
      { root: chatLogRef.current },
    );
    observer.observe(bottom);

    return () => observer.disconnect();
  }, [chatLogRef]);

  return (
    <div ref={chatLogRef} className="jr-ai-assist-modal-body">
      <ul className="jr-ai-assist-list-bubble">
        {!isLoading && showHistory && !messages.length && (
          <PromptHistory
            promptHistory={promptHistory}
            runPrompt={(prompt) => runPrompt(prompt, 'freetext')}
          />
        )}

        <MarkdownContext.Provider
          value={{
            onPromptClicked: (prompt) => runPrompt(prompt, 'suggestion'),
            dataClasses: config.dataClasses,
          }}
        >
          {messages.map((message, index) => (
            <Message
              key={index}
              message={message}
              tools={tools}
              componentTools={componentTools}
            />
          ))}
        </MarkdownContext.Provider>

        {systemMessage && <SystemMessageComponent message={systemMessage} />}

        {isLoading && <LoadingSpinner />}
      </ul>

      <div ref={bottomRef} />

      {!isScrolledToBottom && (
        <div className="jr-ai-assist-floating-actions">
          <button
            type="button"
            className="jr-ai-assist-button"
            title={translate('scrollToBottom')}
            onClick={scrollToBottom}
          >
            <i className="jr-ai-icon jr-ai-icon-arrow-down" />
          </button>
        </div>
      )}
    </div>
  );

  function scrollToBottom() {
    chatLogRef.current?.scrollTo({
      top: chatLogRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }
}

interface MessageProps {
  message: Message;
  tools: ResolvedToolDefinition[];
  componentTools: Map<string, ComponentToolDefinition<unknown>>;
}

function Message({ message, tools, componentTools }: MessageProps) {
  if (message.role === 'user') {
    return (
      <>
        {message.content.map((part, index) => (
          <li key={index}>
            <div className="arrow-box">{part.text}</div>
          </li>
        ))}
      </>
    );
  }

  if (message.role === 'assistant') {
    return (
      <>
        {message.content.map((part, index) => (
          <li className="ai" key={`text-${index}`}>
            <div className="arrow-box">
              <ChatMarkdown>{part.text}</ChatMarkdown>
            </div>
          </li>
        ))}

        {message.tool_calls?.map((toolCall) => {
          const componentTool = componentTools.get(toolCall.name);

          return componentTool ? (
            <ComponentToolCall
              key={`tool-${toolCall.id}`}
              tool={componentTool}
              toolCall={toolCall}
            />
          ) : (
            <ToolCallExplanation
              key={`tool-${toolCall.id}`}
              tools={tools}
              toolCall={toolCall}
            />
          );
        })}
      </>
    );
  }

  return null;
}

interface ComponentToolCallProps {
  tool: ComponentToolDefinition<unknown>;
  toolCall: ToolCall;
}

function ComponentToolCall({ tool, toolCall }: ComponentToolCallProps) {
  if (!toolCall.completed) return null;

  const parsed = tool.parameters.safeParse(parseToolArgs(toolCall.arguments));

  if (!parsed.success) return null;

  const { render: Render } = tool;

  return (
    <li className="ai">
      <div className="arrow-box">
        <Render {...parsed.data} />
      </div>
    </li>
  );
}

interface ToolCallExplanationProps {
  tools: ResolvedToolDefinition[];
  toolCall: ToolCall;
}

function ToolCallExplanation({ tools, toolCall }: ToolCallExplanationProps) {
  const tool = tools.find(
    (resolvedTool) => resolvedTool.name === toolCall.name,
  );

  const explanation = tool?.explanation?.(toolCall.arguments);
  if (!explanation) return null;

  return (
    <li className="ai">
      <div className="arrow-box">
        <div className="jr-ai-assist-badge ghost">
          <i
            className={`jr-ai-icon jr-ai-icon-${toolCall.completed ? 'check' : 'spinner'}`}
          />
          <span className="jr-ai-assist-label">{explanation}</span>
        </div>
      </div>
    </li>
  );
}

function ChatMarkdown({ children }: { children: string }) {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>
      {children}
    </Markdown>
  );
}

interface MarkdownLinkProps {
  href?: string;
  title?: string;
  children?: ReactNode;
}

function MarkdownLink({ href, title, children }: MarkdownLinkProps) {
  if (href === '#run-prompt') {
    return <PromptButton>{children} </PromptButton>;
  }

  if (href) {
    const parts = extractDataItemParts(href);

    if (parts) {
      return <DataItemLink {...parts}>{children}</DataItemLink>;
    }
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" title={title}>
      {children}
    </a>
  );
}

const DataItemLink = connect(
  ({
    className,
    id,
    children,
  }: {
    className: string;
    id: string;
    children?: ReactNode;
  }) => {
    const context = useContext(MarkdownContext);
    const dataClass = context?.dataClasses?.find(
      (klass) => klass.name() === className,
    );

    if (dataClass) {
      try {
        const dataItem = dataClass.get(id);

        if (dataItem) {
          const to = dataItem;

          return <LinkTag to={to}>{children}</LinkTag>;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return <a href={notRoutable()}>{children}</a>;

    function notRoutable() {
      return `#_not_routable/${className}/${id}`;
    }
  },
);

const dataItemUrlPattern = /^\/_data-item\/([^/]+)\/([^/]+)$/;

function extractDataItemParts(
  url: string,
): { className: string; id: string } | null {
  const match = url.match(dataItemUrlPattern);

  return match && match[1] && match[2]
    ? { className: match[1], id: match[2] }
    : null;
}

function PromptButton({ children }: { children?: ReactNode }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const context = useContext(MarkdownContext);

  return (
    <button
      type="button"
      className="jr-ai-assist-button"
      ref={buttonRef}
      onClick={onClick}
    >
      <span className="jr-ai-assist-label">{children}</span>
    </button>
  );

  function onClick() {
    const buttonText = buttonRef.current?.textContent;

    if (buttonText) {
      context?.onPromptClicked(buttonText);
    }
  }
}

function LoadingSpinner() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const updateDots = () => setCount(count === 2 ? 0 : count + 1);
    const intervalId = window.setInterval(updateDots, 500);
    return () => clearInterval(intervalId);
  }, [count]);

  return <>{'•'.repeat(count + 1)}</>;
}

function buildComponentTools(
  tools: ToolDefinition<unknown>[] | undefined,
): Map<string, ComponentToolDefinition<unknown>> {
  const map = new Map<string, ComponentToolDefinition<unknown>>();

  for (const tool of tools ?? []) {
    if (tool.render) map.set(tool.name, tool);
  }

  return map;
}

function parseToolArgs(args: string): unknown {
  try {
    return JSON.parse(args || '{}');
  } catch {
    return undefined;
  }
}

function SystemMessageComponent({ message }: { message: SystemMessage }) {
  const { content } = message;
  const messageRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [content]);

  return (
    <li ref={messageRef} className="ai">
      <div className="arrow-box">
        <ChatMarkdown>{content}</ChatMarkdown>
      </div>
    </li>
  );
}
